import { z } from 'zod';
/**
 * COBRANÇA — o registro interno do que a Specializer tem a receber.
 *
 * ## Por que ela existe, tendo o gateway
 * O gateway é o PROCESSADOR. Quem tem de saber se o Escritório X está em dia é
 * este sistema, e por três motivos concretos:
 *
 *  1. **Webhook se perde.** Rede cai, deploy acontece no segundo errado. Sem
 *     registro próprio, o pagamento existe só lá, e aqui o escritório continua
 *     bloqueado — o pior chamado possível, porque o cliente está certo.
 *  2. **Gateway se troca.** Hoje é o Pagar.me; o dia em que não for, o
 *     histórico financeiro não pode ir junto.
 *  3. **Suporte precisa responder.** "O que aconteceu com essa cobrança?" tem
 *     de ter resposta sem abrir o painel de um terceiro.
 *
 * A regra que decorre disso: **o fluxo tem uma direção só** — gateway →
 * webhook → esta tabela → acesso liberado. Nenhuma requisição de usuário
 * consulta o gateway para decidir alguma coisa; se a API deles ficar lenta, o
 * produto inteiro ficaria junto.
 */
/**
 * Quem processou. A coluna existe desde a primeira linha, com um provedor só.
 *
 * É ela que torna barato ligar um segundo provedor depois (PIX pelo banco, por
 * exemplo, se o volume um dia justificar): vira um adaptador novo, não um
 * modelo novo. Acrescentar a coluna depois exigiria migrar linhas que já são
 * histórico financeiro.
 */
export const PROVEDORES_COBRANCA = ['PAGARME'];
export const provedorCobrancaSchema = z.enum(PROVEDORES_COBRANCA);
export const MEIOS_COBRANCA = ['PIX', 'CARTAO', 'BOLETO'];
export const meioCobrancaSchema = z.enum(MEIOS_COBRANCA);
/**
 * As situações de uma cobrança.
 *
 * `CRIADA` e `AGUARDANDO` são distintas de propósito: a primeira é o instante
 * entre gravar aqui e o gateway confirmar que aceitou. Se a chamada falhar no
 * meio, fica uma `CRIADA` órfã — visível, investigável, e nunca confundida com
 * uma cobrança que o cliente já pode pagar.
 *
 * `ESTORNADA` não é `CANCELADA`: cancelar é desistir antes de receber;
 * estornar é devolver o que já entrou. Juntar as duas apagaria do histórico a
 * diferença entre "não cobrei" e "cobrei e devolvi".
 */
export const SITUACOES_COBRANCA = [
    'CRIADA',
    'AGUARDANDO',
    'PAGA',
    'FALHOU',
    'EXPIRADA',
    'CANCELADA',
    'ESTORNADA',
    /**
     * O cliente CONTESTOU na operadora e o dinheiro foi retirado à força.
     *
     * Separada de `ESTORNADA` porque a diferença importa para quem opera:
     * estorno é decisão NOSSA (devolvemos), chargeback é decisão do cliente
     * contra nós. O dinheiro sai igual nos dois, mas só um é sinal de fraude ou
     * de cliente insatisfeito — juntá-los apagaria do histórico exatamente o
     * caso que alguém precisa ir olhar.
     */
    'CHARGEBACK',
    /**
     * Entrou dinheiro, mas MENOS do que a cobrança pede.
     *
     * Não é `AGUARDANDO` — aguardando é "nada chegou", e tratar as duas como a
     * mesma coisa esconderia que existe dinheiro do cliente parado na conta.
     * Também não é `PAGA`: o escritório continua devendo, e liberar acesso aqui
     * seria dar o mês por um pagamento parcial.
     *
     * É o único estado do fluxo que sempre pede DECISÃO humana: cobrar a
     * diferença ou devolver o que veio. Por isso não é terminal.
     */
    'PARCIAL',
];
export const situacaoCobrancaSchema = z.enum(SITUACOES_COBRANCA);
export const ROTULO_SITUACAO_COBRANCA = {
    CRIADA: 'Preparando',
    AGUARDANDO: 'Aguardando pagamento',
    PAGA: 'Paga',
    FALHOU: 'Não autorizada',
    EXPIRADA: 'Vencida',
    CANCELADA: 'Cancelada',
    ESTORNADA: 'Estornada',
    // Diz o que ACONTECEU, na língua de quem lê a tela. "Chargeback" é palavra
    // de gateway; quem opera o escritório precisa entender sem glossário.
    CHARGEBACK: 'Contestada pelo cliente',
    // "Paga a menor" e não "Parcial": quem lê a tela precisa entender o que
    // aconteceu com o dinheiro dele, não o nome interno do estado.
    PARCIAL: 'Paga a menor',
};
/** Situações a partir das quais nada mais acontece sozinho. */
const TERMINAIS = ['CANCELADA', 'ESTORNADA', 'CHARGEBACK'];
/**
 * Para onde cada situação pode ir.
 *
 * Uma tabela, e não `if`s espalhados pelo serviço: é a lista que responde
 * "isso pode acontecer?" quando um webhook chega fora de ordem — o que
 * acontece o tempo todo, porque a entrega não é garantida em ordem.
 */
const TRANSICOES = {
    CRIADA: ['AGUARDANDO', 'FALHOU', 'CANCELADA'],
    AGUARDANDO: ['PAGA', 'PARCIAL', 'FALHOU', 'EXPIRADA', 'CANCELADA'],
    /*
      De PARCIAL sai-se resolvendo, nunca esquecendo.
  
      `PAGA` é o cliente completando a diferença; `ESTORNADA` é a gente
      devolvendo o que veio; `CANCELADA` é desistir da cobrança. Não volta para
      `AGUARDANDO`: fingir que nada chegou apagaria dinheiro que está na conta.
    */
    PARCIAL: ['PAGA', 'ESTORNADA', 'CANCELADA'],
    /*
      Dinheiro que entrou só sai por DEVOLUÇÃO, e nunca "desfaz" para
      AGUARDANDO. São dois caminhos porque são dois fatos diferentes: estorno é
      devolução nossa; chargeback é retirada forçada pela operadora depois de o
      cliente contestar.
    */
    PAGA: ['ESTORNADA', 'CHARGEBACK'],
    // FALHOU e EXPIRADA voltam para AGUARDANDO: cartão recusado que o cliente
    // retenta, PIX vencido que ganha novo QR. A cobrança é a mesma.
    FALHOU: ['AGUARDANDO', 'PAGA', 'CANCELADA'],
    EXPIRADA: ['AGUARDANDO', 'PAGA', 'CANCELADA'],
    CANCELADA: [],
    ESTORNADA: [],
    CHARGEBACK: [],
};
export function podeTransitar(de, para) {
    // Repetir a mesma situação é sempre aceito e sempre inócuo: é o caso do
    // webhook duplicado, que todo gateway envia.
    if (de === para)
        return true;
    return TRANSICOES[de].includes(para);
}
export function ehTerminal(s) {
    return TERMINAIS.includes(s);
}
/**
 * Os eventos que o sistema entende, já NORMALIZADOS.
 *
 * O vocabulário do gateway não entra no domínio: `charge.paid`,
 * `order.paid`, `charge.payment_failed` e o que mais eles inventarem são
 * traduzidos no adaptador. Deixar o nome deles vazar até aqui amarraria a
 * máquina de estados ao provedor — e o motivo de a coluna `provedor` existir
 * é justamente não estar amarrado.
 */
export const TIPOS_EVENTO_COBRANCA = [
    'REGISTRADA',
    'PAGA',
    'FALHOU',
    'EXPIRADA',
    'ESTORNADA',
    'CANCELADA',
    /** O cliente contestou na operadora e o dinheiro foi retirado. */
    'CONTESTADA',
    /** Entrou menos do que a cobrança pede. Pede decisão humana. */
    'PAGA_A_MENOR',
    /** Recebido, reconhecido como legítimo, e sem significado para o domínio. */
    'IGNORADO',
];
/** Para onde cada evento leva a cobrança. `IGNORADO` não leva a lugar nenhum. */
export const SITUACAO_DO_EVENTO = {
    REGISTRADA: 'AGUARDANDO',
    PAGA: 'PAGA',
    FALHOU: 'FALHOU',
    EXPIRADA: 'EXPIRADA',
    ESTORNADA: 'ESTORNADA',
    CANCELADA: 'CANCELADA',
    CONTESTADA: 'CHARGEBACK',
    PAGA_A_MENOR: 'PARCIAL',
    IGNORADO: null,
};
/**
 * Os dados que o gateway exige para emitir cobrança de pessoa jurídica.
 *
 * Separados do cadastro do escritório de propósito: exigi-los na criação da
 * conta travaria quem só quer experimentar durante o trial — que é justamente
 * quem ainda não decidiu pagar. Eles passam a ser obrigatórios no momento em
 * que alguém pede a primeira cobrança, e nem um segundo antes.
 */
export const dadosDeFaturamentoSchema = z.object({
    cnpj: z.string().regex(/^\d{14}$/, 'CNPJ deve ter 14 dígitos, sem pontuação'),
    emailCobranca: z.string().email(),
});
/**
 * O pedido de cobrança que a tela envia.
 *
 * Só o MEIO: valor, ciclo e pagador saem do estado no servidor. Aceitar o
 * valor do corpo deixaria o navegador escolher quanto paga.
 */
export const pedirCobrancaSchema = z.object({
    meio: meioCobrancaSchema,
});
//# sourceMappingURL=tipos.js.map